/**
 * Cookie Management Utility
 * Helps diagnose and manage cookie sizes to prevent 431 errors
 */

export interface CookieInfo {
  name: string;
  value: string;
  size: number;
}

/**
 * Parse all cookies and return info about each one
 */
export function getAllCookies(): CookieInfo[] {
  if (typeof document === 'undefined') return [];

  const cookieString = document.cookie;
  if (!cookieString) return [];

  const cookies: CookieInfo[] = [];
  const pairs = cookieString.split(';');

  for (const pair of pairs) {
    const trimmed = pair.trim();
    const equalIndex = trimmed.indexOf('=');

    if (equalIndex === -1) continue;

    const name = trimmed.substring(0, equalIndex);
    const value = trimmed.substring(equalIndex + 1);
    const size = name.length + value.length + 2; // +2 for "=" and ";"

    cookies.push({ name, value, size });
  }

  return cookies;
}

/**
 * Calculate total size of all cookies
 */
export function getTotalCookieSize(): number {
  const cookies = getAllCookies();
  return cookies.reduce((total, cookie) => total + cookie.size, 0);
}

/**
 * Get cookies sorted by size (largest first)
 */
export function getCookiesSortedBySize(): CookieInfo[] {
  return getAllCookies().sort((a, b) => b.size - a.size);
}

/**
 * Delete a specific cookie
 */
export function deleteCookie(name: string, path: string = '/', domain?: string): void {
  if (typeof document === 'undefined') return;

  let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  document.cookie = cookieString;

  // Also try without domain
  if (domain) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
  }
}

/**
 * Log cookie diagnostics to console
 */
export function logCookieDiagnostics(): void {
  const cookies = getCookiesSortedBySize();
  const total = getTotalCookieSize();

  console.group('🍪 Cookie Diagnostics');
  console.log(`Total cookie size: ${total} bytes (${(total / 1024).toFixed(2)} KB)`);

  if (total > 4096) {
    console.warn('⚠️ Cookie size exceeds 4KB - may cause issues with some proxies');
  }

  if (total > 8192) {
    console.error('🚨 Cookie size exceeds 8KB - likely to cause 431 errors');
  }

  console.table(
    cookies.map((c) => ({
      name: c.name,
      size: `${c.size} bytes`,
      value: c.value.length > 50 ? c.value.substring(0, 50) + '...' : c.value,
    }))
  );

  console.groupEnd();
}

/**
 * Clean up old or duplicate cookies
 * This is a safe cleanup that only removes obvious duplicates
 */
export function cleanupDuplicateCookies(): void {
  const cookies = getAllCookies();
  const seen = new Set<string>();

  for (const cookie of cookies) {
    if (seen.has(cookie.name)) {
      // Found duplicate - delete it
      console.log(`Removing duplicate cookie: ${cookie.name}`);
      deleteCookie(cookie.name);
    } else {
      seen.add(cookie.name);
    }
  }
}

/**
 * Check if cookies are causing size issues and return recommendations
 */
export function getCookieRecommendations(): string[] {
  const cookies = getCookiesSortedBySize();
  const total = getTotalCookieSize();
  const recommendations: string[] = [];

  if (total > 8192) {
    recommendations.push('🚨 CRITICAL: Total cookie size exceeds 8KB. This will cause 431 errors.');
    recommendations.push('Action: Clear old cookies or reduce JWT token size.');
  } else if (total > 4096) {
    recommendations.push('⚠️ WARNING: Total cookie size exceeds 4KB. May cause issues with some proxies.');
  }

  // Check for suspiciously large individual cookies
  const largeCookies = cookies.filter((c) => c.size > 2048);
  if (largeCookies.length > 0) {
    recommendations.push(`Found ${largeCookies.length} cookie(s) larger than 2KB:`);
    largeCookies.forEach((c) => {
      recommendations.push(`  - ${c.name}: ${c.size} bytes`);
    });
  }

  // Check for duplicate names (shouldn't happen but can)
  const names = cookies.map((c) => c.name);
  const duplicates = names.filter((name, index) => names.indexOf(name) !== index);
  if (duplicates.length > 0) {
    recommendations.push(`Found duplicate cookies: ${duplicates.join(', ')}`);
    recommendations.push('Action: Run cleanupDuplicateCookies()');
  }

  return recommendations;
}
